import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
} from 'react-hook-form';
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	Typography,
} from '@mui/material';
import { Fragment } from 'react';
import { Option } from '../../../api/DTO/common.interface';
import { AppFormLabel } from '../form-view/AppFieldView';

interface AppSingleSelectCheckboxGroupProps {
	label?: string;
	options: Option[];
	disabledOptions?: any[];
	row?: boolean;
	style?: {
		[key: string]: any;
	};
	itemStyle?: {
		[key: string]: any;
	};
	handleChange?: any;
	disabled?: boolean;
	// Text placed between options, for groups that read as one sentence.
	separator?: string;
}

// Checkboxes that behave like a radio group: at most one is ticked, and the
// field holds that option's id rather than an array.
export default function AppSingleSelectCheckboxGroup<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	label,
	options,
	disabledOptions,
	row,
	style,
	itemStyle,
	handleChange,
	disabled = false,
	separator,
	...props
}: AppSingleSelectCheckboxGroupProps &
	UseControllerProps<TFieldValues, TName>) {
	const { field, fieldState } = useController(props);
	const errorMessage = fieldState.error?.message;

	return (
		<FormControl
			sx={{ ...style }}
			fullWidth
			variant='standard'
			error={!!errorMessage}
			disabled={disabled}
		>
			{label && <AppFormLabel>{label}</AppFormLabel>}
			<FormGroup row={row}>
				{options.map((option, index) => (
					<Fragment key={option.id}>
						<FormControlLabel
							sx={{ ...itemStyle }}
							control={
								<Checkbox
									checked={
										field.value != null &&
										String(field.value) === String(option.id)
									}
									onChange={(event) => {
										const value = event.target.checked ? option.id : null;
										field.onChange(value);
										handleChange && handleChange(value);
									}}
								/>
							}
							label={option.name}
							disabled={disabledOptions?.includes(option.id)}
						/>
						{separator && index < options.length - 1 && (
							// Only a leading gap: the checkbox that follows already carries
							// 9px of its own padding.
							<Typography
								component='span'
								sx={{ alignSelf: 'center', marginLeft: '8px' }}
							>
								{separator}
							</Typography>
						)}
					</Fragment>
				))}
			</FormGroup>
			<FormHelperText>{errorMessage}</FormHelperText>
		</FormControl>
	);
}
